const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/actions/fixme.login.ts';
import * as Redux from 'redux';
import { createNoAuthPatapiCall, HttpMethod } from 'App/utils/fetchUtils';
import { IKISAPIAction } from '@cistec/common_web/defs/actionTypes';
import {
    ACTION_TYPE_RESET_LOGIN_STATE,
    ACTION_TYPE_RESET_SESSION_STORAGE,
    ACTION_TYPE_SET_LOGIN_DATA_AT_AUTO_LOGIN,
    REQUEST_ACTION_TYPES_GET_SMS_CODE_TOKEN,
    REQUEST_ACTION_TYPES_SEND_SMS_FROM_INVITATION,
} from './fixme.login.actionTypes';
import { isSecuredLocationPath } from 'App/utils/urlLocationCheck';
import { push } from 'redux-first-history';
import { RESET_STATE } from '@cistec/common_web/actions/userActions';
import { ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_REMOVE_TOKEN } from './patAppNative';

/**
 * Tells the PATAPI to generate an SMS code and text it to the patient's mobile number. PATAPI takes the invitation
 * code (which appears on the notice the patient received by snail mail), and looks for a workitem with a specific
 * TKAT and with the invitation code as TKEY. PATAPI then takes the workitem's PATNR and looks up the mobile numbers
 * in the patient record. If a valid and unique mobile number is found, the SMS is sent to this number, otherwise the
 * call fails.
 * @param invitationCode short run of letters and digits from the notice
 */
export function sendSmsFromInvitation(invitationCode: string) {
    const endpoint = `auth/login/invitation`;
    const meta = { invitationCode };
    const body = { invitationCode };
    return createNoAuthPatapiCall(
        REQUEST_ACTION_TYPES_SEND_SMS_FROM_INVITATION,
        endpoint,
        HttpMethod.POST,
        meta,
        JSON.stringify(body)
    );
}

export type SendSmsFromInvitationAction = IKISAPIAction & { meta: { invitationCode: string } };

export function validateSmsCode(smsCode: string, invitationCode: string) {
    const meta = { invitationCode, smsCode };
    const body = { invitationCode, smsCode };

    const endpoint = `auth/login/validateSmsCode`;
    return createNoAuthPatapiCall(
        REQUEST_ACTION_TYPES_GET_SMS_CODE_TOKEN,
        endpoint,
        HttpMethod.POST,
        meta,
        JSON.stringify(body)
    );
}

export type GetTokenFromSmsCodeAction = IKISAPIAction & { meta: { invitationCode: string; smsCode: string } };

type LoginDataPayload = {
    ['access_token']: string;
    ['token_type']: string;
    ['expires_in']: number;
    kisim: {
        expiresIn: string;
        workId: string;
        aufgebotWorkId: string;
        WIList: { WORKID: string; FALLNR: string }[]; // FIXME: remove?
        patnr: string;
        invitationCode: string;
    };
};

export function setLoginDataAtAutoLogin(loginData: LoginDataPayload) {
    return { type: ACTION_TYPE_SET_LOGIN_DATA_AT_AUTO_LOGIN, payload: loginData };
}

export type SetLoginDataAtAutoLoginAction = ReturnType<typeof setLoginDataAtAutoLogin>;

let isLogoutPending = false;

export function resetLoginState() {
    return (dispatch: Redux.Dispatch<any>, _state: any) => {
        dispatch({ type: ACTION_TYPE_RESET_LOGIN_STATE });
    };
}

/**
 * Revokes the token and logs out
 */
export function logout(timeOutReset?: number) {
    // Using thunk

    return (dispatch: Redux.Dispatch<any>, _state: any) => {
        // Navigate first to welcome screen, then call the logout action. This prevents the current
        // page from thinking it should refetch its objects as they are removed from the state.
        const loginData = JSON.parse(sessionStorage.getItem('login_data') || '{}');

        const requestOptions = {
            headers: {
                Authorization: `${loginData.access_token}`,
                'Content-Type': 'application/json', // Adjust content type if needed
            },
        };

        const dispatchReset = () => {
            // resets all state (almost all)
            sessionStorage.clear();
            document.cookie = 'patapi-session=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
            dispatch({ type: ACTION_TYPE_RESET_LOGIN_STATE });
            dispatch({ type: ACTION_TYPE_RESET_SESSION_STORAGE });
            dispatch({ type: ACTION_TYPE_NATIVE_PATAPP_AUTHORIZE_REMOVE_TOKEN });
            dispatch({ type: RESET_STATE });
        };

        if (!isLogoutPending) {
            isLogoutPending = true;
            fetch('/api/logout', requestOptions)
                .then(response => {
                    let timeout = 420;
                    if (isSecuredLocationPath()) {
                        // this is under secured -> we have to get out there
                        timeout = 1000;
                    } else {
                        dispatch(push('/app/login/invitation-code'));
                    }
                    window.setTimeout(() => {
                        dispatchReset();
                        if (isSecuredLocationPath()) {
                            // need to route outside the hash-container and you can't do this before logout dispatch/resetSessionStorage
                            window.location.replace('/secured/logout');
                        }
                    }, timeout);

                    if (timeOutReset !== undefined) {
                        window.setTimeout(() => {
                            dispatchReset();
                        }, timeOutReset);
                    } else {
                        dispatchReset();
                    }
                })
                .catch(e => console.error(e))
                .finally(() => {
                    isLogoutPending = false;
                });
        }
    };
}

export function actionDevLogin() {
    const meta = { invitationCode: '_DEBUG' };

    const endpoint = `auth/login/development`;
    return createNoAuthPatapiCall(REQUEST_ACTION_TYPES_GET_SMS_CODE_TOKEN, endpoint, HttpMethod.POST, meta);
}
